import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Input, OnChanges } from '@angular/core';
import { AuthenticationService } from '../../core/services/auth.service';
import MetisMenu from 'metismenujs/dist/metismenujs';
import { EventService } from '../../core/services/event.service';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MENU, MENUForCashier, MENUForManager } from './menu';
import { MenuItem } from './menu.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('componentRef') scrollRef: any;
  @Input() isCondensed = false;
  @ViewChild('sideMenu') sideMenu: ElementRef;

  menu: any;
  menuItems: MenuItem[] = [];
  userData: any[] = [];
  phaseLabel: string = '';
  sidebarLoaded = false;

  constructor(
    private eventService: EventService,
    private router: Router,
    public translate: TranslateService,
    private authService: AuthenticationService,
    private http: HttpClient
  ) {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
        this._scrollElement();
      }
    });
  }

  ngOnInit() {
    const sessionData = sessionStorage.getItem(environment.dataKey);
    if (sessionData) {
      this.userData = JSON.parse(atob(sessionData)) || [];
    }

    this.loadPhaseLabel();
  }

  ngAfterViewInit() {
    this.initMetisMenu();
    this._activateMenuDropdown();
  }

  ngOnChanges() {
    if (!this.isCondensed && this.sideMenu || this.isCondensed) {
      setTimeout(() => this.initMetisMenu());
    } else if (this.menu) {
      this.menu.dispose();
    }
  }

  toggleMenu(event: any) {
    event.currentTarget.nextElementSibling.classList.toggle('mm-show');
  }

  initMetisMenu() {
    if (this.sideMenu?.nativeElement) {
      if (this.menu) this.menu.dispose();
      this.menu = new MetisMenu(this.sideMenu.nativeElement);
    }
  }

  _scrollElement() {
    setTimeout(() => {
      const activeEl = document.querySelector(".mm-active") as HTMLElement;
      if (activeEl && this.scrollRef?.SimpleBar) {
        const currentPosition = activeEl.offsetTop;
        if (currentPosition > 500) {
          this.scrollRef.SimpleBar.getScrollElement().scrollTop = currentPosition + 300;
        }
      }
    }, 300);
  }

  _removeAllClass(className: string) {
    const elements = document.getElementsByClassName(className);
    while (elements[0]) {
      elements[0].classList.remove(className);
    }
  }

  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');

    const links = document.getElementsByClassName('side-nav-link-ref');
    const paths = Array.from(links).map(link => link['pathname']);
    let menuItemEl = null;

    const itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substring(0, strIndex);
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }

    if (menuItemEl) {
      menuItemEl.classList.add('active');
      let parent = menuItemEl.parentElement;
      while (parent && parent.id !== 'side-menu') {
        parent.classList.add('mm-active');
        const ul = parent.querySelector('ul');
        if (ul) ul.classList.add('mm-show');

        const anchor = parent.querySelector('.has-arrow, .has-dropdown, .is-parent');
        if (anchor) anchor.classList.add('mm-active');

        parent = parent.parentElement?.closest('li');
      }
    }
  }

  initialize(role: string | undefined, phase: number, isRestaurent: boolean): void {
    switch (role) {
      case 'admin':
        this.menuItems = [...MENU];
        break;
      case 'manager':
        this.menuItems = [...MENUForManager];
        break;
      case 'cashier':
        this.menuItems = [...MENUForCashier];
        break;
      default:
        this.menuItems = [];
    }

    const KOT_LABEL = 'MENUITEMS.KOT.TEXT';

    // Remove KOT for non-restaurants
    this.menuItems = this.menuItems.filter(
      item => item.label !== KOT_LABEL || isRestaurent
    );

    // Filter Phase-specific menu items
    this.menuItems.forEach(menu => {
      if (menu.subItems) {
        menu.subItems = menu.subItems.filter(sub => {
          if (sub.id === 10) {
            return phase === 2;  // show only in Phase 2
          }
          if (phase === 2 && (sub.link === 'sell/selladvance' || sub.link === 'sell/selladvancehistory')) {
            return false;
          }
          if (sub && (sub.link === 'setup/taxes' || sub.label === 'MENUITEMS.SETUP.LIST.SalesTaxes')) {
            return phase !== 2;
          }
          return true;
        });
      }
    });

    this.menuItems = [...this.menuItems];
  }

  loadPhaseLabel() {
    const storecode = this.userData[0]?.storecode;
    if (!storecode) {
      this.phaseLabel = 'Phase Unknown';
      this.sidebarLoaded = true;
      return;
    }

    this.authService.getStoreConfig(storecode).subscribe({
      next: (res) => {
        const phase = Number(res?.phase);
        const isRestaurent = res?.isRestaurent ?? false;
        const role = this.userData[0]?.role;

        this.phaseLabel = phase ? `Phase - ${phase}` : 'Phase Unknown';

        this.initialize(role, phase, isRestaurent);
        this.sidebarLoaded = true;

        setTimeout(() => {
          this.initMetisMenu();
          this._activateMenuDropdown();
        }, 80);
      },

      error: (err) => {
        console.error('Error loading phase config:', err);
        this.phaseLabel = 'Phase Unknown';
        this.initialize(this.userData[0]?.role, 1, false);
        this.sidebarLoaded = true;
      }
    });
  }

  hasItems(item: MenuItem): boolean {
    return !!item.subItems && item.subItems.length > 0;
  }

  isExternalLink(link: string): boolean {
    return link && link.includes('javascript:void(0)');
  }

  openContactUs(): void {
    window.open('https://mofoterpro.com/indexen.html#contact', '_blank');
  }
}
